import React, { FC, useEffect, useMemo, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { Modal } from 'react-bootstrap'
import moment from 'moment'
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table'
import { KTIcon } from '../../../_payairo/helpers'
import { API_BASE_URL } from '../../../config/apiConfig'
import { useAuth } from '../../modules/auth'

const ACCENT = '#064e3b' // Forest Green
const BORDER = '#d1fae5' // Light emerald border

// Define the User type based on the API response structure
type User = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  user_type: string
  is_blocked: boolean
  is_super_admin: boolean
  /** String slug or nested role object from API */
  admin_role: string | null | { slug?: string; display_name?: string }
  date_joined: string
}

/** Normalize `admin_role` whether API sends a string slug or a role object. */
function staffRoleLabel(user: any): string {
  if (user.admin_role?.display_name) return user.admin_role.display_name;
  if (user.role?.display_name) return user.role.display_name;
  if (user.admin_role?.slug) return user.admin_role.slug;
  if (user.role?.slug) return user.role.slug;
  if (typeof user.admin_role === 'string' && user.admin_role.trim()) return user.admin_role.trim();
  if (typeof user.role === 'string' && user.role.trim()) return user.role.trim();
  if (user.is_super_admin) return 'Super Admin';

  const t = user.user_type;
  return (typeof t === 'string' ? t : String(t || '')).trim() || '—';
}

function staffRoleBadgeHighlighted(user: any): boolean {
  const label = staffRoleLabel(user).toLowerCase();
  return label !== '—' && label !== 'customer' && label !== '';
}

const AllUsers: FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const { pathname } = useLocation()
  const { currentUser } = useAuth()

  const isAccessManagement = pathname.includes('access-management')
  const pageTitle = isAccessManagement ? 'Access Management' : 'User Management'

  const fetchUsers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const baseUrl = API_BASE_URL
      const token = localStorage.getItem('token')

      const response = await axios.get(`${baseUrl}/api/v1/users/admin/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.data.ok) {
        setUsers(response.data.data.users)
      } else {
        setError(response.data.message || 'Failed to load user list.')
      }
    } catch (err: any) {
      if (!silent)
        setError(
          err.response?.data?.message || err.message || 'An error occurred while fetching users.'
        )
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    const handleRefresh = () => fetchUsers()
    window.addEventListener('REFRESH_USERS_LIST', handleRefresh)
    return () => window.removeEventListener('REFRESH_USERS_LIST', handleRefresh)
  }, [fetchUsers])

  const handleView = async (id: number) => {
    const tableUser = users.find((u) => u.id === id)
    setSelectedUser(null)
    setShowModal(true)
    try {
      const baseUrl = API_BASE_URL
      const token = localStorage.getItem('token')
      const response = await axios.get(`${baseUrl}/api/r1/admin/users/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.success || response.data.ok || response.data.id) {
        const info = response.data.data?.basic_information || response.data.user || response.data.data?.user || response.data
        setSelectedUser({
          ...tableUser,
          ...info,
          admin_role: info.admin_role || tableUser?.admin_role,
          is_active: !info.is_blocked,
        })
      } else {
        alert(response.data.message || 'Failed to fetch user details')
        setShowModal(false)
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error fetching user details')
      setShowModal(false)
    }
  }

  const handleDeleteClick = (id: number) => {
    setDeleteUserId(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (deleteUserId === null) return;
    const id = deleteUserId;
    setShowDeleteModal(false)

    const previousUsers = [...users]
    setUsers((prev) => prev.filter((u) => u.id !== id))

    try {
      const baseUrl = API_BASE_URL
      const token = localStorage.getItem('token')
      await axios.delete(
        `${baseUrl}/api/r1/admin/users/${id}/delete/`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      fetchUsers(true)
      setDeleteUserId(null)
    } catch (err: any) {
      setUsers(previousUsers)
      alert(err.response?.data?.message || 'Error deleting user')
    }
  }

  const columns: Column<User>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        Cell: ({ value }) => (
          <span className='text-gray-400 fw-bold fs-8 pe-4 font-monospace'>#{value}</span>
        ),
      },
      {
        Header: 'FULL NAME',
        id: 'full_name',
        Cell: ({ row }) => (
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-40px symbol-circle me-4'>
              <div
                className='symbol-label fw-bolder fs-6 shadow-sm border border-light-emerald'
                style={{
                  backgroundColor: BORDER,
                  color: ACCENT,
                }}
              >
                {row.original.first_name?.[0]?.toUpperCase()}
              </div>
            </div>
            <div className='d-flex flex-column'>
              <span
                className='text-gray-900 fw-bolder text-hover-emerald fs-6 mb-1 transition-all cursor-pointer'
                style={{ color: ACCENT }}
              >
                {row.original.first_name} {row.original.last_name}
              </span>
              <span className='text-gray-500 fw-medium fs-8 opacity-75 font-monospace'>
                {row.original.username}
              </span>
            </div>
          </div>
        ),
      },
      {
        Header: 'EMAIL ADDRESS',
        accessor: 'email',
        Cell: ({ value }) => <span className='text-gray-700 fw-medium fs-7'>{value || '—'}</span>,
      },
      {
        Header: 'USER ROLE',
        id: 'user_role_display',
        accessor: (row) => staffRoleLabel(row),
        Cell: ({ row }) => {
          const label = staffRoleLabel(row.original)
          const highlighted = staffRoleBadgeHighlighted(row.original)
          return (
            <span
              className='badge fw-bolder fs-9 px-3 py-2 rounded-1 text-uppercase tracking-widest'
              style={{
                backgroundColor: highlighted ? BORDER : 'rgba(8, 28, 21, 0.05)',
                color: highlighted ? ACCENT : '#5E7E71',
                border: `1px solid ${highlighted ? ACCENT + '20' : 'transparent'}`,
              }}
            >
              {label}
            </span>
          )
        },
      },
      {
        Header: 'STATUS',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <div className='d-flex align-items-center'>
            <span
              className={`w-8px h-8px rounded-circle me-3 ${value ? 'bg-success ripple-active' : 'bg-danger'
                }`}
            ></span>
            <span
              className={`fw-bolder fs-8 text-uppercase tracking-wide ${value ? 'text-success' : 'text-danger'
                }`}
            >
              {value ? 'Active' : 'Inactive'}
            </span>
          </div>
        ),
      },

      {
        Header: 'ACTIONS',
        id: 'actions',
        Cell: ({ row }) => (
          <div className='d-flex flex-column gap-2 align-items-center justify-content-center'>
            <button
              className='btn btn-sm btn-light-emerald btn-hover-emerald w-100px rounded-3 border-0 fw-bolder fs-8 transition-all'
              style={{ backgroundColor: BORDER, color: ACCENT }}
              onClick={(e) => {
                e.stopPropagation()
                handleView(row.original.id)
              }}
            >
              VIEW
            </button>
            <button
              className='btn btn-sm w-100px rounded-3 border-0 fw-bolder fs-8 transition-all btn-hover-danger'
              style={{
                backgroundColor: '#ffe2e5',
                color: '#f64e60',
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(row.original.id)
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffe2e5';
                e.currentTarget.style.color = '#f64e60';
              }}
            >
              DELETE
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const filteredUsers = useMemo(() => {
    if (isAccessManagement) {
      return users.filter((u) => {
        const type = String(u.user_type || '').toLowerCase()
        return type !== 'customer'
      })
    }
    return users
  }, [users, isAccessManagement])

  const table = useTable(
    { columns, data: filteredUsers },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  if (!isAccessManagement) {
    return <div className='p-10 text-center text-gray-500 fw-bold fs-4'>Coming Soon...</div>
  }

  return (
    <div className='mb-10 animate-fade-in'>
      <div className='d-flex flex-wrap justify-content-between align-items-start gap-4 mb-8'>
        <div>
          <h1
            className='fw-bolder fs-1 text-gray-900 mb-2'
            style={{ color: ACCENT, letterSpacing: '-0.02em' }}
          >
            {pageTitle}
          </h1>
          <p className='text-gray-600 fs-6 mb-0 fw-medium'>
            Manage system users and monitor account security status.
          </p>
        </div>
        <div className='d-flex gap-3 align-items-center bg-white p-3 px-6 rounded-4 shadow-sm border border-gray-100'>
          <div className='symbol symbol-40px flex-shrink-0'>
            <div className='symbol-label rounded-3' style={{ backgroundColor: BORDER }}>
              <i className='fas fa-users fs-4' style={{ color: ACCENT }} />
            </div>
          </div>
          <div>
            <div className='text-gray-500 fs-9 fw-bolder text-uppercase tracking-widest'>
              Total Users
            </div>
            <div className='fs-4 fw-bolder' style={{ color: ACCENT }}>
              {filteredUsers?.length}
            </div>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm rounded-4 mb-8 bg-white'>
        <div className='card-body py-6 px-10'>
          <div className='row g-5 align-items-center'>
            <div className='col-lg-12'>
              <div className='position-relative'>
                <i className='fas fa-search position-absolute top-50 translate-middle-y ms-6 text-gray-400 fs-5' />
                <input
                  type='text'
                  className='form-control form-control-solid ps-15 py-4 rounded-3 border-0 bg-light fs-6 fw-bold'
                  placeholder='Search by Full Name, Email Address, or User Role...'
                  value={table.state.globalFilter ?? ''}
                  onChange={(e) => table.setGlobalFilter(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm rounded-4 bg-white overflow-hidden'>
        <div className='card-body p-0'>
          {error && (
            <div className='m-8 alert alert-danger d-flex align-items-center p-5 rounded-3 border-0 bg-light-danger shadow-sm'>
              <i className='fas fa-exclamation-triangle fs-3 text-danger me-4' />
              <span className='fs-7 fw-bold'>{error}</span>
            </div>
          )}

          <div className='table-responsive'>
            <table
              {...table.getTableProps()}
              className='table table-row-bordered table-row-gray-200 align-middle gs-10 gy-6 mb-0'
            >
              <thead className='bg-light bg-opacity-30 border-bottom border-gray-200'>
                <tr className='text-gray-500 fw-bolder fs-9 tracking-widest text-uppercase'>
                  {table.headerGroups.map((headerGroup) =>
                    headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className='py-5'
                      >
                        <div
                          className={`d-flex align-items-center cursor-pointer text-hover-emerald ${column.id === 'actions' ? 'justify-content-center' : ''
                            }`}
                          style={{ transition: 'all 0.2s' }}
                        >
                          {column.render('Header')}
                          {column.isSorted ? (
                            <i
                              className={`fas fa-sort-amount-${column.isSortedDesc ? 'down' : 'up'
                                } ms-2 fs-9 text-emerald`}
                              style={{ color: ACCENT }}
                            />
                          ) : (
                            <i className='fas fa-sort ms-2 fs-9 text-gray-300' />
                          )}
                        </div>
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody {...table.getTableBodyProps()} className='fw-bold'>
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx}>
                      <td colSpan={columns.length} className='py-8 px-10'>
                        <div
                          className='bg-light rounded animate-pulse-light w-100'
                          style={{ height: '30px' }}
                        ></div>
                      </td>
                    </tr>
                  ))
                ) : table.page.length > 0 ? (
                  table.page.map((row) => {
                    table.prepareRow(row)
                    return (
                      <tr
                        {...row.getRowProps()}
                        className='hover-elevated-sm transition-all border-bottom border-gray-100'
                      >
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} className='py-6'>
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className='text-center py-20 bg-light bg-opacity-20'
                    >
                      <i className='fas fa-user-slash fs-2x mb-4 text-gray-300' />
                      <h4 className='fw-bold text-gray-400 fs-5'>No matching records found.</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className='d-flex flex-stack justify-content-between flex-wrap py-8 px-10 bg-light bg-opacity-10 border-top border-gray-200'>
            <div className='fs-8 fw-bolder text-gray-500 text-uppercase tracking-wider'>
              Showing{' '}
              <span className='text-gray-900 fw-bolder'>
                {table.state.pageIndex * table.state.pageSize + 1}
              </span>{' '}
              —{' '}
              <span className='text-gray-900 fw-bolder'>
                {Math.min((table.state.pageIndex + 1) * table.state.pageSize, filteredUsers.length)}
              </span>{' '}
              of <span className='text-gray-900 fw-bolder'>{filteredUsers.length}</span> entries
            </div>

            <div className='d-flex gap-2'>
              <button
                className='btn btn-sm btn-icon btn-light rounded-circle shadow-sm'
                disabled={!table.canPreviousPage}
                onClick={() => table.previousPage()}
              >
                <i className='fas fa-chevron-left text-gray-600' />
              </button>
              <div className='d-flex gap-1'>
                {Array.from({ length: table.pageCount }, (_, i) => i).map((page) => (
                  <button
                    key={page}
                    className={`btn btn-sm btn-icon rounded-circle transition-all ${table.state.pageIndex === page
                        ? 'btn-primary shadow-sm'
                        : 'btn-light text-gray-600'
                      }`}
                    style={{
                      backgroundColor: table.state.pageIndex === page ? ACCENT : undefined,
                      borderColor: table.state.pageIndex === page ? ACCENT : undefined,
                    }}
                    onClick={() => table.gotoPage(page)}
                  >
                    <span className='fw-bolder fs-8'>{page + 1}</span>
                  </button>
                ))}
              </div>
              <button
                className='btn btn-sm btn-icon btn-light rounded-circle shadow-sm'
                disabled={!table.canNextPage}
                onClick={() => table.nextPage()}
              >
                <i className='fas fa-chevron-right text-gray-600' />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size='lg'
        className='modal-user-profile'
        contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
      >
        <div className='modal-content border-0'>
          <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white d-flex align-items-center justify-content-between'>
            <div>
              <h2 className='fw-bolder fs-1 mb-1' style={{ color: ACCENT, letterSpacing: '-0.02em' }}>
                User Profile
              </h2>
              <p className='text-gray-500 fs-7 mb-0 fw-medium'>
                Detailed overview of account information and security status
              </p>
            </div>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-emerald rounded-circle shadow-sm'
              style={{ backgroundColor: BORDER }}
              onClick={() => setShowModal(false)}
            >
              <i className='fas fa-times fs-4' style={{ color: ACCENT }} />
            </button>
          </div>

          <div className='modal-body px-8 pt-6 pb-2 scroll-y' style={{ maxHeight: '70vh' }}>
            {!selectedUser ? (
              <div className='d-flex flex-column align-items-center py-20 text-gray-400'>
                <span
                  className='spinner-border spinner-border-sm mb-4'
                  role='status'
                  style={{ color: ACCENT }}
                />
                <span className='fw-bolder fs-8 tracking-widest text-uppercase'>
                  Fetching Details...
                </span>
              </div>
            ) : (
              <div className='mb-12'>
                <div className='d-flex align-items-center gap-3 mb-8'>
                  <div className='h-2px w-30px rounded' style={{ backgroundColor: ACCENT }} />
                  <h3
                    className='fw-bold fs-4 mb-0 text-uppercase'
                    style={{ color: ACCENT, letterSpacing: '0.05em' }}
                  >
                    Account Details
                  </h3>
                </div>

                <div className='row g-6'>
                  <div className='col-md-6'>
                    <ThemeDetailRow label='Account ID' value={`#${selectedUser.id}`} isEmerald />
                  </div>
                  <div className='col-md-6'>
                    <ThemeDetailRow label='Username' value={selectedUser.username} isMonospace />
                  </div>
                  <div className='col-md-6'>
                    <ThemeDetailRow
                      label='Full Name'
                      value={`${selectedUser.first_name} ${selectedUser.last_name}`}
                      isBolder
                    />
                  </div>
                  <div className='col-md-6'>
                    <ThemeDetailRow label='Email Address' value={selectedUser.email || '—'} />
                  </div>

                  <div className='col-md-6'>
                    <ThemeDetailRow
                      label='User Role'
                      value={
                        <span
                          className='badge fs-9 fw-bolder px-3 py-2 rounded-1 text-uppercase tracking-widest'
                          style={{
                            backgroundColor: staffRoleBadgeHighlighted(selectedUser)
                              ? BORDER
                              : 'rgba(8, 28, 21, 0.05)',
                            color: staffRoleBadgeHighlighted(selectedUser) ? ACCENT : '#5E7E71',
                          }}
                        >
                          {staffRoleLabel(selectedUser)}
                        </span>
                      }
                    />
                  </div>

                  <div className='col-md-6'>
                    <ThemeDetailRow
                      label='Blocked Status'
                      value={
                        <span
                          className='badge fs-9 fw-bolder px-3 py-2 rounded-1 text-uppercase'
                          style={{
                            backgroundColor: selectedUser.is_blocked ? '#ffe2e5' : '#f0f5f1',
                            color: selectedUser.is_blocked ? '#f64e60' : '#28a745',
                          }}
                        >
                          {selectedUser.is_blocked ? 'BLOCKED' : 'CLEAR'}
                        </span>
                      }
                    />
                  </div>

                  <div className='col-md-6'>
                    <ThemeDetailRow
                      label='Super Admin'
                      value={selectedUser.is_super_admin ? 'AUTHORIZED' : 'RESTRICTED'}
                    />
                  </div>

                  <div className='col-md-6'>
                    <ThemeDetailRow
                      label='Joined On'
                      value={moment(selectedUser.date_joined).format('DD MMM YYYY, hh:mm A')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='modal-footer border-0 bg-white pt-6 px-8 pb-8 d-flex justify-content-between align-items-center shadow-top-sm'>
            <div>
              {currentUser?.is_super_admin && selectedUser && (
                <button
                  type='button'
                  className='btn btn-danger px-8 rounded-3 fw-bolder shadow-sm transition-all btn-hover-danger'
                  style={{ backgroundColor: '#ffe2e5', color: '#f64e60' }}
                  onClick={() => {
                    setShowModal(false)
                    handleDeleteClick(selectedUser.id)
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc3545';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffe2e5';
                    e.currentTarget.style.color = '#f64e60';
                  }}
                >
                  DELETE USER
                </button>
              )}
            </div>
            <button
              type='button'
              className='btn btn-primary px-12 rounded-3 fw-bolder shadow-sm'
              style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
              onClick={() => setShowModal(false)}
            >
              Close Details
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size='sm'
        className='modal-delete-confirm'
        contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
      >
        <div className='modal-content border-0'>
          <div className='modal-body px-8 pt-8 pb-8 text-center'>
            <div className='mb-4 d-flex justify-content-center'>
              <div className='d-flex align-items-center justify-content-center rounded-circle' style={{ width: '60px', height: '60px', backgroundColor: '#ffe2e5' }}>
                <i className='fas fa-exclamation-triangle fs-2' style={{ color: '#f64e60' }} />
              </div>
            </div>
            <h3 className='fw-bolder fs-3 mb-2 text-gray-900'>Confirm Deletion</h3>
            <p className='text-gray-500 fs-6 fw-medium mb-8'>
              Are you sure you want to permanently delete this user? This action cannot be undone.
            </p>
            <div className='d-flex justify-content-center gap-3'>
              <button
                type='button'
                className='btn btn-light px-6 rounded-3 fw-bolder shadow-sm'
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-danger px-8 rounded-3 fw-bolder shadow-sm transition-all btn-hover-danger'
                style={{ backgroundColor: '#dc3545' }}
                onClick={confirmDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .ripple-active { animation: ripple 2s infinite linear; }
        @keyframes ripple { 0% { box-shadow: 0 0 0 0 rgba(71, 190, 125, 0.2); } 70% { box-shadow: 0 0 0 10px rgba(71, 190, 125, 0); } 100% { box-shadow: 0 0 0 0 rgba(71, 190, 125, 0); } }

        .animate-pulse-light { animation: pulseBg 1.5s infinite ease-in-out; }
        @keyframes pulseBg { 0% { opacity: 0.4; } 50% { opacity: 0.8; } 100% { opacity: 0.4; } }

        .hover-elevated-sm:hover {
           background-color: #fcfcfd !important;
           transform: translateY(-1px);
           box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.035) !important;
        }

        .shadow-top-sm {
           box-shadow: 0 -1px 3px rgba(0,0,0,0.02);
        }

        .btn-hover-warning:hover {
          background-color: #f6c000 !important;
          color: #000 !important;
        }
        .btn-hover-danger:hover {
          background-color: #dc3545 !important;
          color: #fff !important;
        }
        .btn-hover-success:hover {
          background-color: #28a745 !important;
          color: #fff !important;
        }
        .btn-hover-emerald:hover {
          background-color: ${ACCENT} !important;
          color: #fff !important;
        }
      `}</style>
    </div>
  )
}

const ThemeDetailRow: FC<{
  label: string
  value: React.ReactNode
  isEmerald?: boolean
  isBolder?: boolean
  isMonospace?: boolean
}> = ({ label, value, isEmerald, isBolder, isMonospace }) => (
  <div className='p-5 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm h-100'>
    <label
      className='text-gray-500 fw-bolder text-uppercase mb-2 d-block'
      style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
    >
      {label}
    </label>
    <div
      className={`lh-sm ${isEmerald
          ? 'fs-5 fw-boldest'
          : isBolder
            ? 'fs-5 fw-bolder text-gray-900'
            : 'fs-6 fw-bold text-gray-700'
        }`}
      style={{
        color: isEmerald ? '#064e3b' : undefined,
        fontFamily: isMonospace ? 'monospace' : 'inherit',
      }}
    >
      {value}
    </div>
  </div>
)

export default AllUsers
